import type { LoadDetailData } from "../components/loads/load-detail-types";
import type { UnifiedApprovalRow } from "../core/approvals/unified-approvals";
import { buildAgenda, type AgendaItem } from "../core/console/agenda";
import { buildCaseFile } from "../core/console/case-file";
import { buildMarginReport } from "../core/console/margin-reports";
import type { EvidenceRecord, ExecutionException, OperatingLoad } from "../core/tma";
import type { AgentState, AgentStateLabel } from "../core/tma/agent-state";

export const DEMO_TENANT = {
  tenantId: "tenant_demo_pilot",
  tenantName: "Northstar Brokerage",
  tenantSlug: "demo",
  analyticsOptIn: false,
  role: "admin" as const,
  products: ["haulbase_agent", "full_agentic_tms"] as const,
};

type ScenarioStatus = "monitored" | "approval_required" | "draft_exception" | "operator_review";

export interface DemoScenario {
  id: string;
  reference: string;
  customerName: string;
  carrierName: string;
  carrierMc: string;
  route: string;
  lane: string;
  agentState: AgentState;
  label: AgentStateLabel;
  status: ScenarioStatus;
  lifecycleState: OperatingLoad["lifecycleState"];
  origin: OperatingLoad["origin"] & { timezone?: string };
  destination: OperatingLoad["destination"] & { timezone?: string };
  pickupWindowStart: string;
  pickupWindowEnd: string;
  deliveryWindowStart: string;
  deliveryWindowEnd: string;
  equipmentType: string;
  commodity: string;
  valueCents: number;
  riskScore: number;
  confidenceScore: number;
  trackingAgeHours: number;
  sopGaps: string[];
  decisionLine: string;
  why: string;
  recommendedAction: string;
  requestedAction: string;
  draftPayload: Record<string, unknown>;
  evidence: Array<{
    type: string;
    source: string;
    severity: "info" | "low" | "medium" | "high";
    summary: string;
    payload: Record<string, unknown>;
  }>;
  exceptions: Array<{
    type: string;
    severity: "low" | "medium" | "high" | "critical";
    title: string;
    recommendedAction: string;
  }>;
  charges: Array<{
    side: "customer" | "carrier";
    chargeType: string;
    description: string;
    amountCents: number;
    status: string;
  }>;
  paymentHolds?: Array<{ reason: string; note: string; placedBy: string; placedAt: number }>;
}

const baseTime = Date.parse("2026-05-25T14:00:00.000Z");

export const DEMO_SCENARIOS: DemoScenario[] = [
  {
    id: "demo-late-pickup",
    reference: "NSB-1860",
    customerName: "Cold Chain Foods",
    carrierName: "Lakeview Express",
    carrierMc: "MC-555102",
    route: "Milwaukee, WI -> Memphis, TN",
    lane: "Milwaukee, WI to Memphis, TN",
    agentState: "escalated",
    label: "Decision needed",
    status: "approval_required",
    lifecycleState: "covered",
    origin: { city: "Milwaukee", state: "WI", timezone: "America/Chicago" },
    destination: { city: "Memphis", state: "TN", timezone: "America/Chicago" },
    pickupWindowStart: "2026-05-25T15:00:00.000Z",
    pickupWindowEnd: "2026-05-25T17:00:00.000Z",
    deliveryWindowStart: "2026-05-26T13:00:00.000Z",
    deliveryWindowEnd: "2026-05-26T16:00:00.000Z",
    equipmentType: "reefer",
    commodity: "frozen_food",
    valueCents: 240000,
    riskScore: 91,
    confidenceScore: 82,
    trackingAgeHours: 1,
    sopGaps: [],
    decisionLine: "Approve the drafted customer impact note and recovery check-call plan.",
    why: "Pickup ETA slipped outside the customer threshold and the consignee appointment is tight.",
    recommendedAction: "Approve edited customer update, request revised ETA from carrier, and keep tender unchanged until operator review.",
    requestedAction: "send_customer_update_draft",
    draftPayload: {
      mode: "read_and_draft",
      customerDraftHeld: true,
      message: "Draft customer impact update held for approval; no customer message sent.",
      nextInternalTask: "Call carrier for revised pickup ETA and recovery plan.",
    },
    evidence: [
      {
        type: "eta_prediction",
        source: "generic_tms_sandbox",
        severity: "high",
        summary: "Pickup ETA is 74 minutes after the customer tolerance window.",
        payload: { predictedPickupEta: "2026-05-25T18:14:00.000Z", customerToleranceMinutes: 30 },
      },
      {
        type: "carrier_reply",
        source: "carrier_email_parse",
        severity: "medium",
        summary: "Carrier reports dock delay on prior stop and cannot guarantee pickup before cutoff.",
        payload: { priorStopStatus: "dock_delay", externalMessageSent: false },
      },
    ],
    exceptions: [
      {
        type: "core.customer_impact",
        severity: "critical",
        title: "Late pickup may affect customer delivery window",
        recommendedAction: "Approve the customer impact draft or escalate to managed ops.",
      },
    ],
    charges: [
      { side: "customer", chargeType: "linehaul", description: "Customer linehaul", amountCents: 240000, status: "drafted" },
      { side: "carrier", chargeType: "linehaul", description: "Carrier buy", amountCents: 197500, status: "estimated" },
    ],
  },
  {
    id: "demo-risky-carrier",
    reference: "NSB-1842",
    customerName: "Cold Chain Foods",
    carrierName: "Prairie Line Transport",
    carrierMc: "MC-771204",
    route: "Milwaukee, WI -> Memphis, TN",
    lane: "Milwaukee, WI to Memphis, TN",
    agentState: "needs_approval",
    label: "Decision needed",
    status: "approval_required",
    lifecycleState: "covered",
    origin: { city: "Milwaukee", state: "WI", timezone: "America/Chicago" },
    destination: { city: "Memphis", state: "TN", timezone: "America/Chicago" },
    pickupWindowStart: "2026-05-25T18:00:00.000Z",
    pickupWindowEnd: "2026-05-25T20:00:00.000Z",
    deliveryWindowStart: "2026-05-26T18:00:00.000Z",
    deliveryWindowEnd: "2026-05-26T21:00:00.000Z",
    equipmentType: "reefer",
    commodity: "food_grade",
    valueCents: 228000,
    riskScore: 84,
    confidenceScore: 87,
    trackingAgeHours: 2,
    sopGaps: [],
    decisionLine: "Review carrier identity evidence before any tender or customer commitment.",
    why: "Dispatcher contact changed recently and does not match the carrier profile.",
    recommendedAction: "Hold carrier commitment for operator approval and call the known carrier profile contact.",
    requestedAction: "tender_carrier",
    draftPayload: {
      mode: "read_and_draft",
      tenderHeld: true,
      carrierTenderSent: false,
      verificationAction: "Call carrier profile contact before commitment.",
    },
    evidence: [
      {
        type: "fmcsa_check",
        source: "generic_verification_sandbox",
        severity: "high",
        summary: "Dispatcher identity mismatch against recent provider profile.",
        payload: { verdict: "risky", alerts: ["identity_mismatch", "contact_recently_changed"], confidenceScore: 0.87 },
      },
      {
        type: "document",
        source: "insurance_certificate_parse",
        severity: "low",
        summary: "Insurance active, but certificate contact differs from dispatch contact.",
        payload: { insuranceStatus: "active", certificateContactMatch: false },
      },
    ],
    exceptions: [
      {
        type: "core.carrier_identity_risk",
        severity: "high",
        title: "Carrier identity requires verification",
        recommendedAction: "Call known carrier profile contact and keep tender in draft.",
      },
    ],
    charges: [
      { side: "customer", chargeType: "linehaul", description: "Customer linehaul", amountCents: 228000, status: "drafted" },
      { side: "carrier", chargeType: "linehaul", description: "Carrier buy", amountCents: 181000, status: "estimated" },
    ],
  },
  {
    id: "demo-missing-pod",
    reference: "NSB-1857",
    customerName: "Summit Retail",
    carrierName: "Evergreen Regional",
    carrierMc: "MC-391875",
    route: "Laredo, TX -> Atlanta, GA",
    lane: "Laredo, TX to Atlanta, GA",
    agentState: "needs_approval",
    label: "Decision needed",
    status: "approval_required",
    lifecycleState: "pod_collecting",
    origin: { city: "Laredo", state: "TX", timezone: "America/Chicago" },
    destination: { city: "Atlanta", state: "GA", timezone: "America/New_York" },
    pickupWindowStart: "2026-05-23T14:00:00.000Z",
    pickupWindowEnd: "2026-05-23T16:00:00.000Z",
    deliveryWindowStart: "2026-05-24T17:00:00.000Z",
    deliveryWindowEnd: "2026-05-24T19:00:00.000Z",
    equipmentType: "dry_van",
    commodity: "retail_goods",
    valueCents: 175000,
    riskScore: 79,
    confidenceScore: 90,
    trackingAgeHours: 6,
    sopGaps: [],
    decisionLine: "Approve the payment hold and carrier POD request draft.",
    why: "Delivery is marked complete, but no POD is attached and carrier settlement would otherwise proceed.",
    recommendedAction: "Keep carrier payment on hold, send a driver POD upload link draft, and notify AP internally.",
    requestedAction: "place_carrier_payment_hold",
    draftPayload: {
      mode: "read_and_draft",
      paymentHoldDrafted: true,
      paymentReleased: false,
      driverUploadLinkDrafted: "/upload/pod/demo",
    },
    evidence: [
      {
        type: "document",
        source: "generic_tms_sandbox",
        severity: "high",
        summary: "Delivered status has no POD document attached after the settlement threshold.",
        payload: { deliveredAt: "2026-05-24T18:10:00.000Z", podDocumentId: null, settlementBlocked: true },
      },
    ],
    exceptions: [
      {
        type: "core.missing_pod",
        severity: "high",
        title: "Missing POD blocks carrier payment",
        recommendedAction: "Approve payment hold and request POD upload before settlement.",
      },
    ],
    charges: [
      { side: "customer", chargeType: "linehaul", description: "Customer linehaul", amountCents: 175000, status: "drafted" },
      { side: "carrier", chargeType: "linehaul", description: "Carrier settlement", amountCents: 139500, status: "held" },
    ],
    paymentHolds: [
      {
        reason: "missing_pod",
        note: "Delivered load has no POD attached; settlement remains in draft-and-hold.",
        placedBy: "haulbase-agent",
        placedAt: baseTime + 12_000,
      },
    ],
  },
  {
    id: "demo-provider-conflict",
    reference: "NSB-1858",
    customerName: "Acme Foods",
    carrierName: "Metro Reefer Group",
    carrierMc: "MC-638102",
    route: "Chicago, IL -> Dallas, TX",
    lane: "Chicago, IL to Dallas, TX",
    agentState: "needs_approval",
    label: "Decision needed",
    status: "operator_review",
    lifecycleState: "in_transit",
    origin: { city: "Chicago", state: "IL", timezone: "America/Chicago" },
    destination: { city: "Dallas", state: "TX", timezone: "America/Chicago" },
    pickupWindowStart: "2026-05-25T08:00:00.000Z",
    pickupWindowEnd: "2026-05-25T10:00:00.000Z",
    deliveryWindowStart: "2026-05-26T18:00:00.000Z",
    deliveryWindowEnd: "2026-05-26T21:00:00.000Z",
    equipmentType: "reefer",
    commodity: "produce",
    valueCents: 198000,
    riskScore: 76,
    confidenceScore: 58,
    trackingAgeHours: 9,
    sopGaps: [],
    decisionLine: "Choose which provider signal to trust before updating the customer.",
    why: "ELD and TMS feeds disagree on location by 148 miles, and the TMS timestamp is stale.",
    recommendedAction: "Call driver, annotate the conflict, and hold any ETA/customer draft until confidence recovers.",
    requestedAction: "request_eta",
    draftPayload: {
      mode: "read_and_draft",
      customerEtaHeld: true,
      providerConflict: true,
      staleProvider: "generic_tms_sandbox",
    },
    evidence: [
      {
        type: "eld_position",
        source: "generic_eld_sandbox",
        severity: "medium",
        summary: "ELD places truck near Little Rock with current timestamp.",
        payload: { location: "Little Rock, AR", observedAt: "2026-05-25T13:55:00.000Z" },
      },
      {
        type: "manual_note",
        source: "generic_tms_sandbox",
        severity: "high",
        summary: "TMS still shows pickup yard departure and has not updated in nine hours.",
        payload: { location: "Chicago, IL", staleHours: 9, conflictMiles: 148 },
      },
    ],
    exceptions: [
      {
        type: "core.provider_conflict",
        severity: "high",
        title: "Conflicting provider data lowers ETA confidence",
        recommendedAction: "Call driver and hold customer update draft until provider conflict is resolved.",
      },
    ],
    charges: [
      { side: "customer", chargeType: "linehaul", description: "Customer linehaul", amountCents: 198000, status: "drafted" },
      { side: "carrier", chargeType: "linehaul", description: "Carrier buy", amountCents: 159000, status: "estimated" },
    ],
  },
  {
    id: "demo-stale-tracking",
    reference: "NSB-1849",
    customerName: "Summit Retail",
    carrierName: "Evergreen Regional",
    carrierMc: "MC-391875",
    route: "Laredo, TX -> Atlanta, GA",
    lane: "Laredo, TX to Atlanta, GA",
    agentState: "planning",
    label: "Handling it",
    status: "draft_exception",
    lifecycleState: "in_transit",
    origin: { city: "Laredo", state: "TX", timezone: "America/Chicago" },
    destination: { city: "Atlanta", state: "GA", timezone: "America/New_York" },
    pickupWindowStart: "2026-05-25T09:00:00.000Z",
    pickupWindowEnd: "2026-05-25T11:00:00.000Z",
    deliveryWindowStart: "2026-05-26T15:00:00.000Z",
    deliveryWindowEnd: "2026-05-26T18:00:00.000Z",
    equipmentType: "dry_van",
    commodity: "retail_goods",
    valueCents: 166000,
    riskScore: 62,
    confidenceScore: 82,
    trackingAgeHours: 7,
    sopGaps: [],
    decisionLine: "Draft an internal stale-tracking task for operations review.",
    why: "Tracking is outside the customer cadence threshold.",
    recommendedAction: "Draft a check-call task; do not send a customer message.",
    requestedAction: "request_tracking",
    draftPayload: { mode: "read_and_draft", internalTaskDrafted: true, customerMessageSent: false },
    evidence: [
      {
        type: "manual_note",
        source: "generic_tms_sandbox",
        severity: "medium",
        summary: "No tracking update inside the customer cadence threshold.",
        payload: { lastTrackingAt: "2026-05-25T06:30:00.000Z", staleTrackingHours: 7 },
      },
    ],
    exceptions: [
      {
        type: "core.tracking_gap",
        severity: "medium",
        title: "Tracking update is stale",
        recommendedAction: "Draft internal check-call task and wait for carrier reply.",
      },
    ],
    charges: [
      { side: "customer", chargeType: "linehaul", description: "Customer linehaul", amountCents: 166000, status: "drafted" },
      { side: "carrier", chargeType: "linehaul", description: "Carrier buy", amountCents: 132000, status: "estimated" },
    ],
  },
  {
    id: "demo-sop-gap",
    reference: "NSB-1851",
    customerName: "Northstar Foods",
    carrierName: "Metro Reefer Group",
    carrierMc: "MC-638102",
    route: "Chicago, IL -> Dallas, TX",
    lane: "Chicago, IL to Dallas, TX",
    agentState: "planning",
    label: "Handling it",
    status: "operator_review",
    lifecycleState: "order_received",
    origin: { city: "Chicago", state: "IL", timezone: "America/Chicago" },
    destination: { city: "Dallas", state: "TX", timezone: "America/Chicago" },
    pickupWindowStart: "2026-05-25T20:00:00.000Z",
    pickupWindowEnd: "2026-05-25T22:00:00.000Z",
    deliveryWindowStart: "2026-05-27T13:00:00.000Z",
    deliveryWindowEnd: "2026-05-27T16:00:00.000Z",
    equipmentType: "reefer",
    commodity: "food_grade",
    valueCents: 205000,
    riskScore: 68,
    confidenceScore: 88,
    trackingAgeHours: 1,
    sopGaps: ["seal_capture"],
    decisionLine: "Route SOP mismatch to managed operator review.",
    why: "Customer requires seal capture before dispatch and the load record is missing that tag.",
    recommendedAction: "Draft internal task to collect seal capture before dispatch packet release.",
    requestedAction: "generate_bol",
    draftPayload: { mode: "read_and_draft", dispatchPacketHeld: true, missingSopTags: ["seal_capture"] },
    evidence: [
      {
        type: "composition_decision",
        source: "customer_sop_engine",
        severity: "medium",
        summary: "Customer requires seal capture before dispatch; load record is missing the tag.",
        payload: { requiredSopTags: ["reefer_temp_check", "seal_capture"], actualSopTags: ["reefer_temp_check"] },
      },
    ],
    exceptions: [
      {
        type: "core.sop_gap",
        severity: "medium",
        title: "Customer SOP gap before dispatch",
        recommendedAction: "Collect missing seal capture before releasing dispatch packet.",
      },
    ],
    charges: [
      { side: "customer", chargeType: "linehaul", description: "Customer linehaul", amountCents: 205000, status: "drafted" },
      { side: "carrier", chargeType: "linehaul", description: "Carrier buy", amountCents: 164000, status: "estimated" },
    ],
  },
];

export function getDemoAgenda() {
  return buildAgenda({
    items: DEMO_SCENARIOS.map(toAgendaItem),
    activeCount: DEMO_SCENARIOS.length,
    handledCount: 12,
  });
}

export function getDemoApprovals(tenantId = DEMO_TENANT.tenantId): UnifiedApprovalRow[] {
  return DEMO_SCENARIOS.filter((scenario) => scenario.label === "Decision needed").map((scenario, index) => ({
    id: `demo_approval_${scenario.id}`,
    source: "approval" as const,
    tenantId,
    loadId: scenario.id,
    loadRef: scenario.reference,
    title: `${scenario.reference}: ${scenario.exceptions[0]?.title ?? "Review required"}`,
    requestedAction: scenario.requestedAction,
    status: "pending",
    risk: `${scenario.riskScore}% risk / ${scenario.confidenceScore}% confidence`,
    riskSummary: `${scenario.riskScore}% risk / ${scenario.confidenceScore}% confidence. ${scenario.why}`,
    recommendation: scenario.recommendedAction,
    draftPayload: scenario.draftPayload,
    createdAt: baseTime + index * 60_000,
  }));
}

export function getDemoLoadDetail(loadId: string, tenantId = DEMO_TENANT.tenantId): LoadDetailData {
  const scenario = DEMO_SCENARIOS.find((item) => item.id === loadId) ?? DEMO_SCENARIOS[0];
  const caseFile = buildCaseFile({
    reference: scenario.reference,
    route: scenario.route,
    customerName: scenario.customerName,
    agentState: scenario.agentState,
    label: scenario.label,
    pendingDecision: scenario.decisionLine,
    openExceptionAction: scenario.recommendedAction,
    predictionWhy: scenario.why,
  });

  return {
    agentState: scenario.agentState,
    label: scenario.label,
    caseFile,
    load: {
      _id: scenario.id,
      tenantId,
      reference: scenario.reference,
      lifecycleState: scenario.lifecycleState,
      customerName: scenario.customerName,
      carrierName: scenario.carrierName,
      carrierMc: scenario.carrierMc,
      origin: scenario.origin,
      destination: scenario.destination,
      pickupWindowStart: scenario.pickupWindowStart,
      pickupWindowEnd: scenario.pickupWindowEnd,
      deliveryWindowStart: scenario.deliveryWindowStart,
      deliveryWindowEnd: scenario.deliveryWindowEnd,
      equipmentType: scenario.equipmentType,
      equipment: { type: scenario.equipmentType, payload: { source: "demo_workspace" } },
      commodity: { category: scenario.commodity, payload: { customerSensitive: true } },
      regulatoryTags: scenario.equipmentType === "reefer" ? ["food_grade"] : [],
      customerReferences: [{ label: "PO", value: `${scenario.reference}-PO`, requiredBy: scenario.customerName }],
      loadCategory: "spot",
      serviceLevel: scenario.riskScore > 80 ? "expedited" : "standard",
      blindMode: "none",
      billToParty: "real_shipper",
      valueCents: scenario.valueCents,
      currentRiskScore: scenario.riskScore,
      currentConfidenceScore: scenario.confidenceScore,
    },
    charges: scenario.charges.map((charge, index) => ({ _id: `${scenario.id}_charge_${index}`, ...charge })),
    exceptions: scenario.exceptions.map((exception, index) => ({
      _id: `${scenario.id}_exception_${index}`,
      status: "open",
      ...exception,
    })),
    approvals:
      scenario.label === "Decision needed"
        ? [
            {
              _id: `demo_approval_${scenario.id}`,
              title: `${scenario.reference}: ${scenario.exceptions[0]?.title ?? "Review required"}`,
              requestedAction: scenario.requestedAction,
              status: "pending",
              riskSummary: `${scenario.riskScore}% risk / ${scenario.confidenceScore}% confidence. ${scenario.why}`,
            },
          ]
        : [],
    evidence: scenario.evidence.map((item, index) => ({
      _id: `${scenario.id}_evidence_${index}`,
      type: item.type,
      source: item.source,
      createdAt: baseTime + index * 45_000,
      payload: {
        severity: item.severity,
        summary: item.summary,
        reasoning: item.summary,
        ...item.payload,
      },
    })),
    agentActions: [
      {
        _id: `${scenario.id}_action_0`,
        action: scenario.requestedAction,
        status: "queued",
        permissionDecision: scenario.label === "Decision needed" ? "approval_required" : "block",
        createdAt: baseTime + 90_000,
        output: {
          reasoning: scenario.recommendedAction,
          evidenceIds: scenario.evidence.map((_, index) => `${scenario.id}_evidence_${index}`),
          noUnsafeExternalActionSent: true,
          operatingMode: "read_and_draft",
        },
      },
    ],
    agentTasks: [
      {
        _id: `${scenario.id}_task_0`,
        taskId: `${scenario.id}_task_0`,
        type: scenario.requestedAction,
        status: scenario.label === "Decision needed" ? "needs_approval" : "open",
        priority: scenario.riskScore > 85 ? "critical" : scenario.riskScore > 70 ? "high" : "medium",
        title: scenario.exceptions[0]?.title ?? "Agent review",
        recommendedAction: scenario.recommendedAction,
        createdAt: baseTime + 95_000,
        payload: {
          riskSummary: scenario.why,
          noUnsafeExternalActionSent: true,
          operatingMode: "read_and_draft",
        },
      },
    ],
    agentDecisions: [
      {
        _id: `${scenario.id}_decision_0`,
        decisionId: `${scenario.id}_decision_0`,
        decision: scenario.label === "Decision needed" ? "approval_required" : "draft",
        confidenceScore: scenario.confidenceScore,
        riskScore: scenario.riskScore,
        reasoningSummary: scenario.why,
        risks: [scenario.why],
        recommendedNextAction: scenario.recommendedAction,
        requiredApproval: true,
        policyChecks: [
          {
            ruleId: "read_and_draft_default",
            outcome: "approval_required",
            reason: "read_and_draft mode requires human review before external action",
          },
        ],
        toolCalls: ["create_draft_recommendation"],
        createdAt: baseTime + 100_000,
      },
    ],
    toolCallLogs: [
      {
        _id: `${scenario.id}_tool_0`,
        toolCallId: `${scenario.id}_tool_0`,
        toolName: "create_draft_recommendation",
        status: "approval_required",
        permissionDecision: "approval_required",
        input: scenario.draftPayload,
        output: {
          operatingMode: "read_and_draft",
          noUnsafeExternalActionSent: true,
        },
        createdAt: baseTime + 105_000,
      },
    ],
    loadRiskAssessments: [
      {
        _id: `${scenario.id}_risk_0`,
        riskScore: scenario.riskScore,
        confidenceScore: scenario.confidenceScore,
        factors: scenario.evidence.map((item) => ({ code: item.type, label: item.summary, severity: item.severity })),
        recommendedNextAction: scenario.recommendedAction,
        recommendation: scenario.riskScore >= 80 ? "approval_required" : "allow",
        createdAt: baseTime + 110_000,
      },
    ],
    auditEvents: [
      {
        _id: `${scenario.id}_audit_0`,
        eventType: "agent.workflow.output_recorded",
        actorType: "tma",
        summary: `${scenario.reference} routed as ${scenario.status}; no unsafe external action sent.`,
        createdAt: baseTime + 120_000,
        payload: {
          decision: scenario.label === "Decision needed" ? "approval_required" : "draft",
          operatingMode: "read_and_draft",
          noUnsafeExternalActionSent: true,
          evidenceRetained: true,
        },
      },
    ],
    paymentHolds: scenario.paymentHolds?.map((hold, index) => ({ _id: `${scenario.id}_hold_${index}`, ...hold })),
  };
}

export function getDemoAgentOpsSnapshot() {
  return {
    metrics: [
      { title: "Event volume", value: "612/hr" },
      { title: "P95 workflow latency", value: "1.7s" },
      { title: "Retry rate", value: "1.4%" },
      { title: "Dead-letter rate", value: "0.2%" },
    ],
    providerHealth: [
      { provider: "generic_tms_sandbox", status: "degraded", latency: "2.4s", staleSignals: 2 },
      { provider: "generic_verification_sandbox", status: "healthy", latency: "820ms", staleSignals: 0 },
      { provider: "generic_eld_sandbox", status: "healthy", latency: "510ms", staleSignals: 0 },
    ],
    tenantRows: [
      {
        tenant: DEMO_TENANT.tenantName,
        throughput: 524,
        retries: 6,
        deadLetters: 1,
        backlog: getDemoApprovals().length,
        status: "production_read_and_draft",
      },
      {
        tenant: "Beta Foods Logistics",
        throughput: 88,
        retries: 2,
        deadLetters: 0,
        backlog: 1,
        status: "shadow_mode",
      },
    ],
    incidents: [
      { title: "TMS feed stale for NSB-1858", severity: "warn" as const },
      { title: "Dead-letter spike: malformed TMS event", severity: "critical" as const },
    ],
  };
}

export function getConsoleSnapshot() {
  const loads = DEMO_SCENARIOS.map(toOperatingLoad);
  const evidence = DEMO_SCENARIOS.flatMap(toEvidenceRecords);
  const charges = DEMO_SCENARIOS.flatMap((scenario) =>
    scenario.charges.map((charge) => ({
      loadId: scenario.id,
      side: charge.side,
      amountCents: charge.amountCents,
    })),
  );

  return {
    now: new Date().toISOString(),
    loads: loads.map((load) => ({
      load,
      evidence: evidence.filter((item) => item.loadId === load.id),
      exceptions: (DEMO_SCENARIOS.find((scenario) => scenario.id === load.id)?.exceptions ?? []).map((exception, index) => ({
        id: `${load.id}_exception_${index}`,
        tenantId: DEMO_TENANT.tenantId,
        loadId: load.id,
        type: exception.type,
        severity: exception.severity,
        status: "open" as const,
        title: exception.title,
        detectedReason: exception.title,
        recommendedAction: exception.recommendedAction,
        createdFromEvidenceIds: [`${load.id}_evidence_0`],
        slaDueAt: "2026-05-25T18:00:00.000Z",
      })) satisfies ExecutionException[],
      confidence: {
        loadHealthScore: 100 - (load.currentRiskScore ?? 0),
        visibilityConfidence: load.currentConfidenceScore ?? 0,
        etaConfidence: load.currentConfidenceScore ?? 0,
        exceptionRiskScore: load.currentRiskScore ?? 0,
        fieldConfidence: {},
        computedFromEvidenceIds: [],
      },
    })),
    loadBoard: [
      {
        provider: "DAT",
        loadRef: "NSB-1860",
        status: "draft_hold",
        capacity: 8,
        bestFit: "Known reefer carrier, requires operator approval",
        targetRate: "$1,975",
      },
    ],
    approvals: getDemoApprovals(),
    policies: [
      "V1 runs in read_and_draft.",
      "Customer messages, carrier tenders, money movement, and unsafe external actions require approval.",
      "No unsafe external action is sent from the local demo workspace.",
    ],
    permissionExamples: [
      {
        decision: "approval_required",
        requestedAutonomyMode: "read_and_draft",
        grantedAutonomyMode: "read_and_draft",
        reasons: ["Customer-facing and carrier-commitment actions stay draft-and-hold."],
      },
    ],
    marginReports: {
      byCustomer: buildMarginReport(loads, charges, "customer"),
      byLane: buildMarginReport(loads, charges, "lane"),
      byCarrier: buildMarginReport(loads, charges, "carrier"),
    },
  };
}

export function getAgentDemoLoadsFromWorkspace() {
  return DEMO_SCENARIOS.map((scenario) => ({
    id: scenario.id.replace("demo-", "load-"),
    scenarioId: scenario.id,
    reference: scenario.reference,
    customer: scenario.customerName,
    carrier: scenario.carrierName,
    carrierMc: scenario.carrierMc,
    lane: scenario.lane,
    status: "monitored" as const,
    riskScore: scenario.riskScore / 100,
    confidenceScore: scenario.confidenceScore / 100,
    trackingAgeHours: scenario.trackingAgeHours,
    sopGaps: scenario.sopGaps,
    recommendation: scenario.recommendedAction,
    evidence: scenario.evidence.map((item, index) => ({
      id: `${scenario.id}-evidence-${index}`,
      source: item.source,
      category: item.type,
      severity: item.severity,
      summary: item.summary,
      rawPayload: item.payload,
    })),
    draftActions: [],
    audit: [
      {
        at: "2026-05-25T14:00:00.000Z",
        event: "agent.event.persisted",
        summary: `Normalized ${scenario.reference} evidence retained before workflow processing.`,
        payload: { sourceSystem: scenario.evidence[0]?.source ?? "demo", partitionKey: `${DEMO_TENANT.tenantId}/${scenario.reference}` },
      },
    ],
  }));
}

function toAgendaItem(scenario: DemoScenario): AgendaItem {
  return {
    loadId: scenario.id,
    reference: scenario.reference,
    route: scenario.route,
    customerName: scenario.customerName,
    agentState: scenario.agentState,
    label: scenario.label,
    riskScore: scenario.riskScore,
    decisionLine: scenario.decisionLine,
    handlingLine: scenario.recommendedAction,
    why: scenario.why,
  };
}

function toOperatingLoad(scenario: DemoScenario): OperatingLoad & {
  currentRiskScore?: number;
  currentConfidenceScore?: number;
} {
  return {
    id: scenario.id,
    tenantId: DEMO_TENANT.tenantId,
    reference: scenario.reference,
    lifecycleState: scenario.lifecycleState,
    customerName: scenario.customerName,
    carrierName: scenario.carrierName,
    carrierMc: scenario.carrierMc,
    origin: scenario.origin,
    destination: scenario.destination,
    pickupWindowStart: scenario.pickupWindowStart,
    pickupWindowEnd: scenario.pickupWindowEnd,
    deliveryWindowStart: scenario.deliveryWindowStart,
    deliveryWindowEnd: scenario.deliveryWindowEnd,
    equipmentType: scenario.equipmentType,
    valueCents: scenario.valueCents,
    customerSensitive: true,
    equipment: { type: scenario.equipmentType, payload: {} },
    commodity: { category: scenario.commodity, payload: {} },
    regulatoryTags: scenario.equipmentType === "reefer" ? ["food_grade"] : [],
    customerReferences: [{ label: "PO", value: `${scenario.reference}-PO`, requiredBy: scenario.customerName }],
    loadCategory: "spot",
    serviceLevel: scenario.riskScore > 80 ? "expedited" : "standard",
    blindMode: "none",
    billToParty: "real_shipper",
    currentRiskScore: scenario.riskScore,
    currentConfidenceScore: scenario.confidenceScore,
  };
}

function toEvidenceRecords(scenario: DemoScenario): EvidenceRecord[] {
  return scenario.evidence.map((item, index) => ({
    id: `${scenario.id}_evidence_${index}`,
    tenantId: DEMO_TENANT.tenantId,
    loadId: scenario.id,
    type: item.type as EvidenceRecord["type"],
    source: item.source,
    occurredAt: "2026-05-25T14:00:00.000Z",
    receivedAt: "2026-05-25T14:00:10.000Z",
    reliabilityScore: scenario.confidenceScore,
    payload: item.payload,
  }));
}
